import { Center, Flex, Spinner, Text } from '@chakra-ui/react';

import { createColumnHelper } from '@tanstack/react-table';

import { Badge, BadgeStatus } from '@aslanpay/portal-common-lib';

import { Table } from 'app/components/UI/Table/Table';

import { CompanyDeductionsReportStatus } from 'app/constants/deductionsReports';

import useFetchDeductionsReportErrors from 'app/hooks/useFetchDeductionsReportErrors';
import { DeductionsReportError } from 'app/hooks/useFetchDeductionsReportErrors/types';

import { formatReconciliationAmount as formatAmount } from 'app/utils/reconciliations';

const SEVERITY_VALUES: Record<
  DeductionsReportError['severity'],
  { value: string; color: BadgeStatus }
> = {
  ERROR: { value: 'Error', color: 'left' as BadgeStatus },
  WARNING: { value: 'Warning', color: 'warning' as BadgeStatus },
};

const HEADINGS: Partial<Record<CompanyDeductionsReportStatus, string>> = {
  FAILED: 'Why this report failed',
  PROCESSED_WITH_WARNINGS: 'Warnings on this report',
};

/** Names the problems shown for a report with the given status, or nothing when it has none to show. */
export const getDeductionsReportErrorsHeading = (status?: CompanyDeductionsReportStatus) =>
  status ? HEADINGS[status] : undefined;

const columnHelper = createColumnHelper<DeductionsReportError>();

const columns = [
  columnHelper.accessor('severity', {
    header: 'Severity',
    enableSorting: false,
    cell: (info) => (
      <Badge
        value={SEVERITY_VALUES[info.getValue()]?.value ?? info.getValue()}
        status={SEVERITY_VALUES[info.getValue()]?.color}
      />
    ),
  }),
  columnHelper.accessor('payrollId', {
    header: 'Payroll ID',
    enableSorting: false,
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor('expectedAmount', {
    header: "Aslan's records",
    enableSorting: false,
    cell: (info) => formatAmount(info.getValue()),
  }),
  columnHelper.accessor('actualAmount', {
    header: 'On the report',
    enableSorting: false,
    cell: (info) => formatAmount(info.getValue()),
  }),
  columnHelper.accessor('message', {
    header: 'Problem',
    enableSorting: false,
    cell: (info) => (
      <Text maxW="480px" whiteSpace="normal">
        {info.getValue()}
      </Text>
    ),
  }),
];

interface DeductionsReportErrorsProps {
  deductionsReportId?: string;
  status?: CompanyDeductionsReportStatus;
  showHeading?: boolean;
}

/** Explains why a company deductions report failed, or what it was delivered with warnings about. */
function DeductionsReportErrors({
  deductionsReportId = undefined,
  status = undefined,
  showHeading = true,
}: DeductionsReportErrorsProps) {
  const heading = getDeductionsReportErrorsHeading(status);
  const { data, isLoading, isError } = useFetchDeductionsReportErrors(
    deductionsReportId,
    status,
    !!heading
  );

  if (!heading) {
    return null;
  }

  const errors = data?.errors ?? [];

  return (
    <Flex
      mt={showHeading ? '20px' : 0}
      flexDir="column"
      gap="20px"
      data-testid="deductions-report-errors"
    >
      {showHeading && (
        <Text as="h3" textStyle="h3" color="brand.darkPurple">
          {heading}
        </Text>
      )}
      {isLoading && (
        <Center py="20px">
          <Spinner color="brand.purple" />
        </Center>
      )}
      {isError && (
        <Text color="brand.darkGray">
          The reasons could not be loaded. Please refresh the page.
        </Text>
      )}
      {!isLoading && !isError && errors.length === 0 && (
        <Text color="brand.darkGray">No reasons were recorded for this report.</Text>
      )}
      {errors.length > 0 && <Table columns={columns} data={errors} total={errors.length} />}
    </Flex>
  );
}

export default DeductionsReportErrors;
